/**
 * The caller's tenant entitlements, as {module_key: boolean}.
 *
 * One hook rather than the query repeated inline, so every consumer shares a
 * cache entry and the same staleTime — the shell, the module launcher and the
 * Settings tabs must not disagree about which modules a workspace owns, and the
 * moment they read it with different options they can.
 *
 * Display only. This decides what to *show*; what to *allow* is decided by
 * require_module() on the backend, and a UI that hides a field is never the only
 * thing standing between that field and the database.
 *
 * Spec: .claude/specs/module-aware-core-setup.md
 */
import { useQuery } from "@tanstack/react-query";

import { getMyModules } from "../api";
import { useAuth } from "../context/AuthContext";
import { ModuleKey, ModuleMap, ownsModule } from "../moduleMetadata";

export interface UseModulesResult {
  /** Raw map. Undefined until the first fetch resolves. */
  modules: ModuleMap | undefined;
  /** True only once the tenant is known to own the module. */
  owns: (key: ModuleKey) => boolean;
  /** True when the tenant owns at least one of the given modules. */
  ownsAny: (...keys: ModuleKey[]) => boolean;
  isLoading: boolean;
}

export function useModules(): UseModulesResult {
  const { user } = useAuth();
  const { data: modules, isLoading } = useQuery<ModuleMap>({
    queryKey: ["my-modules"],
    queryFn: getMyModules,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // While the map is still loading, `owns` answers false. Fields belonging to a
  // module therefore appear as the answer arrives rather than flashing on screen
  // and being pulled away, which is the more jarring of the two.
  const owns = (key: ModuleKey) => ownsModule(modules, key);

  return {
    modules,
    owns,
    ownsAny: (...keys: ModuleKey[]) => keys.some(owns),
    isLoading,
  };
}
